import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';

/**
 * Authentication Interceptor
 *
 * Adds ext-auth OIDC access token to all requests to Aetheris backend APIs.
 * Handles authentication errors (401/403) by redirecting to login.
 *
 * Token Source: localStorage (from ext-auth OIDC flow)
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check if this is a request to our Aetheris backend API
  const isAetherisBackendRequest =
    req.url.startsWith(environment.api.authUrl) ||
    req.url.startsWith(environment.api.apiUrl) ||
    req.url.startsWith(environment.api.translationUrl);

  // Skip non-backend requests
  if (!isAetherisBackendRequest) {
    return next(req);
  }

  // Skip auth/token endpoint requests (to avoid circular dependency)
  const isPublicEndpoint =
    req.url.includes('/ext-auth/token') ||
    req.url.includes('/ext-auth/authorize') ||
    req.url.includes('/api/app/version'); // Allow version info for footer

  if (isPublicEndpoint) {
    return next(req);
  }

  // Get ID token from auth service
  const idToken = authService.getIdToken();

  // If no token available, return error but don't force redirect
  // The authGuard handles route protection; interceptor shouldn't disrupt flows like OIDC.
  if (!idToken) {
    console.warn('⚠️ No ID token available for backend request');
    return throwError(() => new Error('No authentication token available'));
  }

  // Check if token is expired
  if (!authService.isAuthenticated()) {
    console.warn('🔥 ID token expired - redirecting to login');
    authService.logout();
    router.navigate(['/login']);
    return throwError(() => new Error('Authentication token expired'));
  }

  // Clone request and add authorization header with ID token
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
  };

  // Only set Content-Type for non-FormData requests
  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const authReq = req.clone({
    setHeaders: headers,
  });

  // Handle HTTP response errors
  return next(authReq).pipe(
    catchError((httpError) => {
      // Allow 404 errors for photo endpoints to pass through
      if (httpError.status === 404 && req.url.includes('/photo')) {
        return throwError(() => httpError);
      }

      // Handle 401 Unauthorized - token invalid or expired
      if (httpError.status === 401) {
        console.warn('🔥 API returned 401 - authentication failed');
        authService.logout();
        router.navigate(['/login'], {
          queryParams: { error: 'Session expired. Please login again.' },
        });
        return throwError(() => new Error('Authentication failed'));
      }

      // Handle 403 Forbidden - insufficient permissions
      if (httpError.status === 403) {
        console.warn('🔥 API returned 403 - access forbidden');
        return throwError(() => httpError);
      }

      // Re-throw other HTTP errors
      return throwError(() => httpError);
    })
  );
};
